import React from 'react'
import { AppointmentStatus, CreateAppointmentDto } from '@myapp/contract'
import { GewerkSearchSelect } from '../../../entities/gewerk'
import { Input, FieldWrapper, Modal } from '../../../shared/ui'
import { formatDateDDMMYYYY, toDatetimeLocal, fromDatetimeLocal } from '../../../shared/helpers/date'
import { AttachmentsSection } from './AttachmentsSection'
import { useCanRescheduleCompleted } from '../../../shared/hooks/useUserRoles'

const formatTime = (iso: string): string => {
  const d = new Date(iso)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

const formatDateTimeDisplay = (iso: string | null | undefined): string => {
  if (!iso) return '—'
  return `${formatDateDDMMYYYY(iso)}, ${formatTime(iso)}`
}

const addMinutesIso = (iso: string, minutes: number): string => {
  const d = new Date(iso)
  d.setTime(d.getTime() + minutes * 60_000)
  return d.toISOString()
}

const clampNonNeg = (n: number): number => (n < 0 ? 0 : n)

const diffMinutes = (fromIso: string, toIso: string): number =>
  Math.round((new Date(toIso).getTime() - new Date(fromIso).getTime()) / 60_000)

interface Durations {
  plannedTravelDuration: number
  plannedWorkDuration: number
  plannedDepartureDuration: number
}

interface ActualDurations {
  actualTravelDuration: number
  actualWorkDuration: number
  actualDepartureDuration: number | null
}

interface Props {
  show: boolean
  setShow: (show: boolean) => void
  draft: CreateAppointmentDto
  technicianName: string | null
  status?: AppointmentStatus
  description?: string | null
  onDescriptionChange?: (value: string) => void
  readonlyDurations?: boolean
  onDurationsChange?: (durations: Durations) => void
  actualDurations?: ActualDurations | null
  onActualDurationsChange?: (durations: ActualDurations) => void
  onTradeCodeChange: (tradeCode: number) => void
  files: File[]
  onFilesChange: (files: File[]) => void
  existingAttachments: string[]
  onRemoveExistingAttachment: (index: number) => void
  technicianComment?: string | null
  followUpReason?: string | null
  isFullDay?: boolean
  onFullDayChange?: (v: boolean) => void
  onSave?: () => void
  canSave?: boolean
  isPending?: boolean
  mode?: 'create' | 'edit'
  collisionWarning?: boolean
  validationError?: string | null
  backendError?: string | null
  onUmdisponieren?: () => void
  title?: string
  actualStart?: string | null
  actualEnd?: string | null
  reschedulingRequestedAt?: string | null
  onPlannedStartChange?: (iso: string) => void
  onActualStartChange?: (iso: string) => void
  onActualEndChange?: (iso: string) => void
}

const SectionHeader: React.FC<{ label: string }> = ({ label }) => (
  <div className="mt-1 pt-2 border-t border-gray-200">
    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
      {label}
    </span>
  </div>
)

export const DraftAppointmentForm: React.FC<Props> = (props) => {
  const {
    show, setShow, draft, technicianName,
    status,
    description,
    readonlyDurations = false,
    actualDurations,
    technicianComment,
    isFullDay = false,
    mode = 'edit',
    isPending = false,
    canSave = true,
    collisionWarning = false,
    validationError = null,
    backendError = null,
    actualStart,
    actualEnd,
    reschedulingRequestedAt,
  } = props

  const isCompleted = status === 'COMPLETED' || status === 'COMPLETED_WITH_FOLLOW_UP'
  const canRescheduleCompleted = useCanRescheduleCompleted()
  const editableAsCompleted = isCompleted && canRescheduleCompleted

  const modalTitle = props.title
    ?? (status === 'COMPLETED_WITH_FOLLOW_UP'
      ? editableAsCompleted ? 'Abgeschlossenen Termin verschieben' : 'Termindetails (Folgetermin erforderlich)'
      : isCompleted
        ? editableAsCompleted ? 'Abgeschlossenen Termin verschieben' : 'Termindetails (Abgeschlossen)'
        : readonlyDurations ? 'Termindetails' : mode === 'edit' ? 'Termin bearbeiten' : 'Termin erstellen')

  const saveLabel = mode === 'edit' ? 'Aktualisieren' : 'Speichern'

  const handleSubmitWithConfirm = React.useCallback(() => {
    if (!props.onSave) return
    if (isCompleted) {
      if (typeof window !== 'undefined' && !window.confirm('Dieser Termin ist abgeschlossen. Wirklich verschieben?')) return
    }
    props.onSave()
  }, [props, isCompleted])

  const submitProps = (!isCompleted || editableAsCompleted) && props.onSave
    ? {
        submitButton: { onSubmit: handleSubmitWithConfirm, title: saveLabel, isPending, disabled: !canSave },
      }
    : {}

  const actionButtons = props.onUmdisponieren ? (
    <button
      type="button"
      onClick={props.onUmdisponieren}
      className="w-full h-[50px] max-[1535px]:h-[42px] rounded-[30px] border-2 border-[#16499A] bg-transparent font-['PT_Sans'] font-bold text-xl max-[1535px]:text-[17px] text-[#16499A] transition-colors hover:bg-[#16499A]/[0.08]"
    >
      Termin umdisponieren
    </button>
  ) : undefined

  const canEditPlannedStart = (!isCompleted || editableAsCompleted) && !isFullDay && Boolean(props.onPlannedStartChange)
  const canEditPlannedDurations = !readonlyDurations && !isFullDay && Boolean(props.onDurationsChange)
  const canEditActualStart = Boolean(props.onActualStartChange)
  const canEditActualEnd = Boolean(props.onActualEndChange)
  const canEditActualDurations = Boolean(props.onActualDurationsChange)

  const plannedTotalMinutes =
    draft.plannedTravelDuration + draft.plannedWorkDuration + draft.plannedDepartureDuration
  const plannedEndIso = draft.plannedStart
    ? addMinutesIso(draft.plannedStart, plannedTotalMinutes)
    : null

  const showActualSection = Boolean(actualStart) || editableAsCompleted

  const handlePlannedStartTyped = (v: string) => {
    if (!canEditPlannedStart) return
    const iso = fromDatetimeLocal(v)
    if (iso) props.onPlannedStartChange!(iso)
  }
  const handlePlannedEndTyped = (v: string) => {
    if (!canEditPlannedDurations) return
    const iso = fromDatetimeLocal(v)
    if (!iso || !draft.plannedStart) return
    const workDuration = clampNonNeg(
      diffMinutes(draft.plannedStart, iso)
        - draft.plannedTravelDuration
        - draft.plannedDepartureDuration,
    )
    props.onDurationsChange!({
      plannedTravelDuration: draft.plannedTravelDuration,
      plannedWorkDuration: workDuration,
      plannedDepartureDuration: draft.plannedDepartureDuration,
    })
  }
  const handleActualStartTyped = (v: string) => {
    if (!canEditActualStart) return
    const iso = fromDatetimeLocal(v)
    if (!iso) return
    props.onActualStartChange!(iso)
    if (actualDurations && props.onActualEndChange) {
      const abfahrt = actualDurations.actualDepartureDuration ?? 0
      props.onActualEndChange(addMinutesIso(iso, actualDurations.actualWorkDuration + abfahrt))
    }
  }
  const handleActualEndTyped = (v: string) => {
    if (!canEditActualEnd) return
    const iso = fromDatetimeLocal(v)
    if (!iso) return
    props.onActualEndChange!(iso)
    if (actualStart && actualDurations && props.onActualDurationsChange) {
      const abfahrt = actualDurations.actualDepartureDuration ?? 0
      const work = clampNonNeg(diffMinutes(actualStart, iso) - abfahrt)
      props.onActualDurationsChange({
        ...actualDurations,
        actualWorkDuration: work,
      })
    }
  }

  return (
    <Modal
      show={show}
      setShow={setShow}
      title={modalTitle}
      actionButtons={actionButtons}
      className="w-full sm:min-w-[520px] lg:min-w-[720px] max-w-[92vw]"
      {...submitProps}
    >
      <div className="flex flex-col gap-3 sm:gap-4 py-2 max-[1535px]:py-0">

        {/* ── Row 1: identity ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 lg:gap-x-6 gap-y-3 sm:gap-y-4">
          <FieldWrapper label="Auftrag">
            <Input value={draft.workOrderId} disabled />
          </FieldWrapper>
          <FieldWrapper label="Techniker">
            <Input value={technicianName ?? '—'} disabled />
          </FieldWrapper>
          <FieldWrapper label="Gewerk">
            <GewerkSearchSelect
              value={draft.tradeCode}
              onChange={(id) => props.onTradeCodeChange(id ?? 0)}
              disabled={isCompleted && !editableAsCompleted}
            />
          </FieldWrapper>
        </div>

        {/* ── Section: Geplante Zeit ── */}
        <SectionHeader label="Geplante Zeit" />
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-x-4 lg:gap-x-6 gap-y-3 sm:gap-y-4 items-start">
          <FieldWrapper label="Beginn (geplant)">
            <Input
              type="datetime-local"
              value={toDatetimeLocal(draft.plannedStart)}
              disabled={!canEditPlannedStart}
              onChange={handlePlannedStartTyped}
            />
          </FieldWrapper>

          <div className="grid grid-cols-3 gap-x-2 sm:gap-x-3 md:min-w-[280px] lg:min-w-[320px]">
            <FieldWrapper label="Anfahrt (min)">
              <Input
                type="number"
                value={String(draft.plannedTravelDuration)}
                step={15}
                disabled={!canEditPlannedDurations}
                onChange={(v) => {
                  if (!canEditPlannedDurations) return
                  const n = parseInt(v, 10)
                  if (!isNaN(n) && n >= 0)
                    props.onDurationsChange!({
                      plannedTravelDuration: n,
                      plannedWorkDuration: draft.plannedWorkDuration,
                      plannedDepartureDuration: draft.plannedDepartureDuration,
                    })
                }}
              />
            </FieldWrapper>
            <FieldWrapper label="Arbeit (min)">
              <Input
                type="number"
                value={String(draft.plannedWorkDuration)}
                step={15}
                disabled={!canEditPlannedDurations}
                onChange={(v) => {
                  if (!canEditPlannedDurations) return
                  const n = parseInt(v, 10)
                  if (!isNaN(n) && n >= 15)
                    props.onDurationsChange!({
                      plannedTravelDuration: draft.plannedTravelDuration,
                      plannedWorkDuration: n,
                      plannedDepartureDuration: draft.plannedDepartureDuration,
                    })
                }}
              />
            </FieldWrapper>
            <FieldWrapper label="Abfahrt (min)">
              <Input
                type="number"
                step={15}
                value={String(draft.plannedDepartureDuration)}
                disabled={!canEditPlannedDurations}
                onChange={(v) => {
                  if (!canEditPlannedDurations) return
                  const n = parseInt(v, 10)
                  if (!isNaN(n) && n >= 0)
                    props.onDurationsChange!({
                      plannedTravelDuration: draft.plannedTravelDuration,
                      plannedWorkDuration: draft.plannedWorkDuration,
                      plannedDepartureDuration: n,
                    })
                }}
              />
            </FieldWrapper>
          </div>

          <FieldWrapper label="Ende (geplant)">
            <Input
              type={canEditPlannedDurations ? 'datetime-local' : 'text'}
              value={canEditPlannedDurations
                ? (plannedEndIso ? toDatetimeLocal(plannedEndIso) : '')
                : formatDateTimeDisplay(plannedEndIso)}
              disabled={!canEditPlannedDurations}
              onChange={handlePlannedEndTyped}
            />
          </FieldWrapper>
        </div>

        {/* ── Ganztägig toggle (only when editable) ── */}
        {!isCompleted && props.onFullDayChange && (
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={isFullDay}
                onChange={(e) => props.onFullDayChange!(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-[#16499A] accent-[#16499A] cursor-pointer"
              />
              <span className="text-sm font-medium text-gray-700">Ganztägig</span>
            </label>
          </div>
        )}

        {/* ── Section: Ist-Zeit ── */}
        {showActualSection && (
          <>
            <SectionHeader label="Ist-Zeit" />
            <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-x-4 lg:gap-x-6 gap-y-3 sm:gap-y-4 items-start">
              <FieldWrapper label="Beginn (ist)">
                <Input
                  type="datetime-local"
                  value={actualStart ? toDatetimeLocal(actualStart) : ''}
                  disabled={!canEditActualStart}
                  placeholder="—"
                  onChange={handleActualStartTyped}
                />
              </FieldWrapper>

              <div className="grid grid-cols-3 gap-x-2 sm:gap-x-3 md:min-w-[280px] lg:min-w-[320px]">
                <FieldWrapper label="Anfahrt (ist, min)">
                  <Input
                    type="number"
                    step={15}
                    value={actualDurations != null ? String(actualDurations.actualTravelDuration) : ''}
                    placeholder="—"
                    disabled={!canEditActualDurations}
                    onChange={(v) => {
                      if (!canEditActualDurations || !actualDurations) return
                      const n = parseInt(v, 10)
                      if (!isNaN(n) && n >= 0)
                        props.onActualDurationsChange!({
                          ...actualDurations,
                          actualTravelDuration: n,
                        })
                    }}
                  />
                </FieldWrapper>
                <FieldWrapper label="Arbeit (ist, min)">
                  <Input
                    type="number"
                    step={15}
                    value={actualDurations != null ? String(actualDurations.actualWorkDuration) : ''}
                    placeholder="—"
                    disabled={!canEditActualDurations}
                    onChange={(v) => {
                      if (!canEditActualDurations || !actualDurations) return
                      const n = parseInt(v, 10)
                      if (isNaN(n) || n < 0) return
                      props.onActualDurationsChange!({
                        ...actualDurations,
                        actualWorkDuration: n,
                      })
                      if (actualStart && props.onActualEndChange) {
                        const abfahrt = actualDurations.actualDepartureDuration ?? 0
                        props.onActualEndChange(addMinutesIso(actualStart, n + abfahrt))
                      }
                    }}
                  />
                </FieldWrapper>
                <FieldWrapper label="Abfahrt (ist, min)">
                  <Input
                    type="number"
                    step={15}
                    value={actualDurations?.actualDepartureDuration != null ? String(actualDurations.actualDepartureDuration) : ''}
                    placeholder="—"
                    disabled={!canEditActualDurations}
                    onChange={(v) => {
                      if (!canEditActualDurations || !actualDurations) return
                      const n = parseInt(v, 10)
                      if (isNaN(n) || n < 0) return
                      props.onActualDurationsChange!({
                        ...actualDurations,
                        actualDepartureDuration: n,
                      })
                      if (actualStart && props.onActualEndChange) {
                        props.onActualEndChange(addMinutesIso(actualStart, actualDurations.actualWorkDuration + n))
                      }
                    }}
                  />
                </FieldWrapper>
              </div>

              <FieldWrapper label="Ende (ist)">
                <Input
                  type={canEditActualEnd ? 'datetime-local' : 'text'}
                  value={canEditActualEnd
                    ? (actualEnd ? toDatetimeLocal(actualEnd) : '')
                    : formatDateTimeDisplay(actualEnd)}
                  disabled={!canEditActualEnd}
                  placeholder="—"
                  onChange={handleActualEndTyped}
                />
              </FieldWrapper>
            </div>
          </>
        )}

        {/* ── Umdisponierung beantragt am (only when set) ── */}
        {reschedulingRequestedAt && (
          <FieldWrapper label="Umdisponierung beantragt am" className="sm:max-w-[50%]">
            <Input
              value={`${formatDateDDMMYYYY(reschedulingRequestedAt)}, ${formatTime(reschedulingRequestedAt)}`}
              disabled
            />
          </FieldWrapper>
        )}

        {/* ── Beschreibung ── */}
        {(description != null || props.onDescriptionChange) && (
          <FieldWrapper label="Beschreibung">
            <Input
              as="textarea"
              value={description ?? ''}
              disabled={isCompleted || !props.onDescriptionChange}
              placeholder={props.onDescriptionChange && !isCompleted ? 'Beschreibung (optional)' : undefined}
              inputClassName="!min-h-[80px] max-[1535px]:!min-h-[56px] resize-none"
              onChange={props.onDescriptionChange}
            />
          </FieldWrapper>
        )}

        {collisionWarning && (
          <p className="text-sm text-red-500 font-medium">
            Zeitüberschneidung mit einem bestehenden Termin.
          </p>
        )}
        {validationError && !collisionWarning && (
          <p className="text-xs text-red-500">{validationError}</p>
        )}
        {backendError && (
          <p className="text-sm text-red-500 font-medium">{backendError}</p>
        )}

        {status === 'COMPLETED_WITH_FOLLOW_UP' && props.followUpReason && (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-500">Begründung (Folgetermin)</span>
            <div className="w-full px-3 py-2 text-sm border border-[#ED9E42]/40 rounded-lg bg-[#ED9E42]/5 text-gray-700 whitespace-pre-wrap">
              {props.followUpReason}
            </div>
          </div>
        )}

        {isCompleted && technicianComment && (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-500">Techniker-Kommentar</span>
            <div className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-700 whitespace-pre-wrap">
              {technicianComment}
            </div>
          </div>
        )}
      </div>

      {!isCompleted && (
        <AttachmentsSection
          existingAttachments={props.existingAttachments}
          onRemoveExistingAttachment={props.onRemoveExistingAttachment}
          files={props.files}
          onFilesChange={props.onFilesChange}
        />
      )}

      {isCompleted && editableAsCompleted && (
        <AttachmentsSection
          existingAttachments={props.existingAttachments}
          onRemoveExistingAttachment={props.onRemoveExistingAttachment}
          files={props.files}
          onFilesChange={props.onFilesChange}
        />
      )}

      {isCompleted && !editableAsCompleted && props.existingAttachments.length > 0 && (
        <AttachmentsSection
          existingAttachments={props.existingAttachments}
          onRemoveExistingAttachment={() => {}}
          files={[]}
          onFilesChange={() => {}}
          readonly
        />
      )}
    </Modal>
  )
}
